// src/pages/PolicyRegister.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import { orderTypeOptions, policyNameOptions } from "@/constants/selectOptions";
import { useGetMyRentCorpQuery } from "@/redux/rentCorpApi";
import { useGetTermByIdQuery, usePutTermMutation, useDeleteTermMutation, useGetTermListQuery, usePostTermMutation } from "@/redux/termApi";
import { useUploadFileMutation } from "@/redux/uploadApi";

export const PolicyRegister = () => {
    const { termId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 약관 필드
        name: "", // 주차장명
        contractTypes: [],
        mandatoryYn: "",
        exposedYn: "",
        memo: "",
        uploadId: null,
        regiFileName: "",
    });

    const { mode, toastMsg, confirmStatus, addressPopupStatus } = formData;

    // 1) 모드 설정: termId 유무
    useEffect(() => {
        if (termId) {
            console.log(termId);
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: termId,
            }));
        }
    }, [termId, setFormData]);

    // 2) UPDATE 모드일 경우, 약관 상세조회
    const {
        data: termDetail,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetTermByIdQuery(
        { termId },
        {
            skip: !termId, // termId 없으면 호출X
        }
    );

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (termDetail && termDetail.data) {
            const d = termDetail.data;
            console.log(d);
            setFormData((prev) => ({
                ...prev,
                ...d,
                uploadId: d.upload?.id,
            }));
        }
    }, [termDetail, setFormData]);

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    // 4) 내 소속 렌트카 업체 조회
    const { data: myRentCorpData } = useGetMyRentCorpQuery();
    const corpOptions =
        myRentCorpData?.data?.map((item) => ({
            value: item.id,
            label: item.corpName,
        })) || [];

    // 5) post / put mutation
    const [postParkingLot, { isLoading: isPostLoading }] = usePostTermMutation();
    const [putParkingLot, { isLoading: isPutLoading }] = usePutTermMutation();
    const [deleteTerm, { isLoading: isDeleteLoading }] = useDeleteTermMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                rentCorpId: formData.rentCorpId,
                contractTypes: formData.contractTypes,
                mandatoryYn: formData.mandatoryYn,
                exposedYn: formData.exposedYn,
                memo: formData.memo,
                uploadId: formData.uploadId,
            };

            if (mode === "UPDATE") {
                // 수정
                console.log("termId:", formData.id);
                console.log("requestBody:", requestBody);
                await putParkingLot({
                    termId: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await postParkingLot(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/admin/policy-management"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {
        // 유효성 검사
        if (!formData.rentCorpId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자를 선택해주세요.",
            }));
            return;
        }

        if (!formData.name) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "약관 명칭을 선택해주세요.",
            }));
            return;
        }
        if (!formData.mandatoryYn) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "필수/선택 여부를 선택해주세요.",
            }));
            return;
        }

        if (!formData.exposedYn) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "활성/비활성 여부를 선택해주세요.",
            }));
            return;
        }

        if (!formData.contractTypes || formData.contractTypes.length === 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "적용 계약 유형을 1개 이상 선택해주세요.",
            }));
            return;
        }

        if (!formData.uploadId && !formData.upload) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "약관 파일을 첨부해주세요.",
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `약관 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 8) 삭제 Confirm
    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
            isLoading: true,
        }));

        try {
            await deleteTerm(formData.id).unwrap();
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "삭제가 완료되었습니다.",
                    confirmDescription: "",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => navigate("/admin/policy-management"),
                        },
                    ],
                },
            }));
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
                toastMsg: error.message || "삭제 중 오류가 발생했습니다.",
            }));
        }
    };

    // 9) 삭제 버튼
    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 약관 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">약관 {mode === "CREATE" ? "등록" : "상세"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-cancel" onClick={handleDelete}>
                                    약관 삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">약관 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <TermsForm
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

const TermsForm = ({ formData, handleChange, setFormData, corpOptions }) => {
    const { name, contractTypes, mandatoryYn, exposedYn, memo } = formData;
    const termFileRef = useRef(null);

    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

    const handleFileChange = async (e) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);
            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;
            setFormData((prev) => ({
                ...prev,
                uploadId: newUploadId,
                fileName: fileObj.name,
                upload: null, // 기존 upload 객체 초기화
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const getFileName = () => {
        // 파일 업로드 시에는 fileName을, 상세조회 시에는 upload.fileName을 사용
        return formData.fileName || formData.upload?.fileName || "";
    };

    // 파일첨부 버튼
    const handleButtonClick = () => {
        termFileRef.current.click();
    };

    return (
        <>
            {/* 사업자 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 선택"
                            value={corpOptions.find((opt) => opt.value === formData.rentCorpId)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    rentCorpId: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            {/* 약관 명칭 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>약관 명칭</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={policyNameOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="약관 명칭 선택"
                            value={policyNameOptions.find((opt) => opt.value === formData.name)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    name: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            {/* 필수/선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>필수/선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="mandatoryYn"
                                id="mandatoryYn_Y"
                                checked={mandatoryYn === "Y"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        mandatoryYn: "Y",
                                    }));
                                }}
                            />
                            <label htmlFor="mandatoryYn_Y">필수</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="mandatoryYn"
                                id="mandatoryYn_N"
                                checked={mandatoryYn === "N"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        mandatoryYn: "N",
                                    }));
                                }}
                            />
                            <label htmlFor="mandatoryYn_N">선택</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 활성/비활성 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>활성/비활성</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="exposedYn"
                                id="exposedYn_Y"
                                checked={exposedYn === "Y"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        exposedYn: "Y",
                                    }));
                                }}
                            />
                            <label htmlFor="exposedYn_Y">활성화</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="exposedYn"
                                id="exposedYn_N"
                                checked={exposedYn === "N"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        exposedYn: "N",
                                    }));
                                }}
                            />
                            <label htmlFor="exposedYn_N">비활성화</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 적용 계약 유형 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>적용 계약 유형</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {orderTypeOptions.map((option) => (
                            <div className="checkbox" key={option.value}>
                                <input
                                    type="checkbox"
                                    id={`contractType_${option.value}`}
                                    name="contractTypes"
                                    value={option.value}
                                    checked={contractTypes && contractTypes.includes(option.value)}
                                    onChange={(e) => {
                                        const { checked, value } = e.target;
                                        setFormData((prev) => {
                                            const updated = prev.contractTypes ? [...prev.contractTypes] : [];
                                            if (checked) {
                                                if (!updated.includes(value)) {
                                                    updated.push(value);
                                                }
                                            } else {
                                                const index = updated.indexOf(value);
                                                if (index > -1) {
                                                    updated.splice(index, 1);
                                                }
                                            }
                                            return {
                                                ...prev,
                                                contractTypes: updated,
                                            };
                                        });
                                    }}
                                />
                                <label htmlFor={`contractType_${option.value}`}>
                                    <span>{option.label}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 1) 파일 (uploadType = "REGI") */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>파일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName()}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                if (formData.upload && formData.upload.objectUrl) {
                                    window.open(formData.upload.objectUrl, "_blank");
                                } else {
                                    handleButtonClick();
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick()}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={termFileRef}
                            style={{ display: "none" }}
                            accept=".pdf"
                            onChange={(e) => handleFileChange(e)}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>약관 내용</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="약관 내용을 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};
